import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';

interface LinkPreviewProps {
  url: string;
}

interface OGData {
  title: string;
  description: string;
  image: string;
  url: string;
}

const fetchOGData = async (url: string): Promise<OGData | null> => {
  try {
    const res = await fetch(`https://opengraph.io/api/1.1/site/${encodeURIComponent(url)}?app_id=demo`);
    if (!res.ok) return null;
    const data = await res.json();
    return {
      title: data.hybridGraph.title || url,
      description: data.hybridGraph.description || '',
      image: data.hybridGraph.image || '',
      url: data.hybridGraph.url || url,
    };
  } catch {
    return null;
  }
};

function getDomain(url: string) {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const LinkPreview: React.FC<LinkPreviewProps> = ({ url }) => {
  const [og, setOg] = useState<OGData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchOGData(url).then((data) => {
      if (mounted) {
        setOg(data);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [url]);

  if (loading) {
    return <CircularProgress size={20} />;
  }

  // Fallback card if no OG data
  if (!og) {
    const domain = getDomain(url);
    return (
      <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 1, mt: 1, maxWidth: 400 }}>
        <img
          src={`https://www.google.com/s2/favicons?sz=64&domain_url=${encodeURIComponent(url)}`}
          alt="Favicon"
          style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, marginRight: 12 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{domain}</a>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{url}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 1, mt: 1, maxWidth: 400 }}>
      {og.image && (
        <img src={og.image} alt="preview" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, marginRight: 12 }} />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <a href={og.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>{og.title}</a>
        </Typography>
        {og.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {og.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{og.url}</Typography>
      </Box>
    </Paper>
  );
};

export default LinkPreview; 